import { useMemo } from "react";
import { useTable, useSortBy } from "react-table";
import { ITableColumn } from "../Table/TableColumn";
import { DefaultCellForColumn } from "../Table/DefaultCellForColumn";
import { DefaultHeaderForColumn } from "../Table/DefaultHeaderForColumn";
import { TableWrapper, TableLoader, TableHeaderCell, Align } from "../Table/style";
import { IPerpDataPairRow } from "../../interfaces/perpData";
import { formatAmount } from "../../utils/strings";
import { getValueColor } from "../PnL/utils";

interface IPerpDataPnlTableProps {
  rows: IPerpDataPairRow[];
  isLoading: boolean;
}

const formatUsd = (value: number) => {
  let prefix = "";
  if (value > 0) prefix = "+";
  else if (value < 0) prefix = "-";
  return `${prefix}$${formatAmount(Math.abs(value))}`;
};

export function PerpDataPnlTable({ rows, isLoading }: IPerpDataPnlTableProps) {
  const columns: ITableColumn<IPerpDataPairRow>[] = useMemo(
    () => [
      {
        title: "Asset",
        accessor: "asset",
        align: "left",
      },
      {
        title: "rPNL",
        accessor: "realizedPnl",
        align: "right",
        Cell: ({ value }) => (
          <Align align="right" style={{ color: getValueColor(Number(value)) }}>
            {formatUsd(Number(value))}
          </Align>
        ),
      },
      {
        title: "Fees",
        accessor: "fees",
        align: "right",
        Cell: ({ value }) => (
          <Align align="right" style={{ color: getValueColor(-Number(value)) }}>
            {formatUsd(-Number(value))}
          </Align>
        ),
      },
      {
        title: "Aevo Fund",
        accessor: "aevoFunding",
        align: "right",
        Cell: ({ value }) => (
          <Align align="right" style={{ color: getValueColor(Number(value)) }}>
            {formatUsd(Number(value))}
          </Align>
        ),
      },
      {
        title: "Binance Fund",
        accessor: "binanceFunding",
        align: "right",
        Cell: ({ value }) => (
          <Align align="right" style={{ color: getValueColor(Number(value)) }}>
            {formatUsd(Number(value))}
          </Align>
        ),
      },
      {
        title: "Bybit Fund",
        accessor: "bybitFunding",
        align: "right",
        Cell: ({ value }) => (
          <Align align="right" style={{ color: getValueColor(Number(value)) }}>
            {formatUsd(Number(value))}
          </Align>
        ),
      },
      {
        title: "Net",
        accessor: "combined",
        align: "right",
        Cell: ({ value }) => (
          <Align align="right" style={{ color: getValueColor(Number(value)), fontWeight: 600 }}>
            {formatUsd(Number(value))}
          </Align>
        ),
      },
    ],
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows: tableRows, prepareRow } =
    useTable(
      {
        columns,
        data: rows,
        defaultColumn: {
          Header: DefaultHeaderForColumn,
          Cell: DefaultCellForColumn,
        } as any,
        autoResetSortBy: false,
        initialState: {
          sortBy: [{ id: "combined", desc: true }],
        },
      },
      useSortBy
    );

  return (
    <TableWrapper>
      <TableLoader isLoading={isLoading} />
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
              {headerGroup.headers.map((column: any) => (
                <TableHeaderCell
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  key={column.id}
                  style={{ width: column.width }}
                >
                  {column.render("Header")}
                </TableHeaderCell>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {tableRows.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()} key={row.id}>
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()} key={cell.column.id}>
                    {cell.render("Cell")}
                  </td>
                ))}
              </tr>
            );
          })}
          {!isLoading && tableRows.length === 0 && (
            <tr>
              <td colSpan={columns.length} style={{ textAlign: "center" }}>
                No perp data available for the selected period.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </TableWrapper>
  );
}
